import React from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, MapPin, Phone, Building2, Tag, Lock, Hourglass, Ticket, ExternalLink,
} from "lucide-react";
import SessionCard from "@/components/SessionCard";
import BookingModal from "@/components/BookingModal";
import SpeakButton from "@/components/SpeakButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_LABEL = { en: "EN", fr: "FR", ar: "AR", pt: "PT" };

export default function SessionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [session, setSession] = React.useState(null);
  const [related, setRelated] = React.useState([]);
  const [modal, setModal] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/sessions/${id}`)
      .then((r) => { if (!cancelled) setSession(r.data); })
      .catch(() => { toast.error("Session not found"); navigate("/", { replace: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, navigate]);

  React.useEffect(() => {
    if (!session?.organization) { setRelated([]); return; }
    axios.get(`${API}/sessions`).then((r) => {
      setRelated(r.data.filter((s) => s.id !== session.id && (s.organization || "") === session.organization));
    }).catch(() => setRelated([]));
  }, [session]);

  if (loading || !session) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="h-64 rounded-2xl bg-white/60 animate-pulse" />
      </div>
    );
  }

  const title = session.title?.[lng] || session.title?.en || "";
  const description = session.description?.[lng] || session.description?.en || "";
  const startTime = new Date(session.starts_at);
  const endTime = new Date(session.ends_at);
  const remaining = session.capacity - session.reserved_count;
  const isClosed = !!session.closed;
  const isTbc = !!session.time_tbc;
  const status = isClosed ? "closed" : isTbc ? "tbc" : (remaining <= 0 ? "full" : remaining <= 5 ? "limited" : "available");
  const isAmber = session.type === "side_event";
  const fmt = (d) => d.toLocaleTimeString(lng, { hour: "numeric", minute: "2-digit", hour12: true });
  const fmtDate = (d) => d.toLocaleDateString(lng, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(`${session.room} Dakar Senegal`)}&output=embed`;
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${session.room} Dakar Senegal`)}`;

  const btnDisabled = isClosed || isTbc || status === "full";
  const btnLabel = isClosed ? "Closed · Invitation only" : isTbc ? "Time TBC · Notify me" : status === "full" ? t("session.full") : t("session.reserve");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Link to="/" data-testid="detail-back" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[color:var(--ink-3)] hover:text-[color:var(--forest)] transition-colors mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to agenda
      </Link>

      <div className="grid md:grid-cols-3 gap-5">
        {/* Main column */}
        <div className="md:col-span-2 space-y-5">
          <div className="diplomatic-card p-5 sm:p-7" data-accent={isAmber ? "amber" : "forest"} data-testid="session-detail-hero">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isAmber ? "bg-[color:var(--amber-soft)] text-[color:var(--amber-dark)]" : "bg-[color:var(--forest-soft)] text-[color:var(--forest-dark)]"}`}>
                {t(`filters.${session.type}`)}
              </span>
              {session.native_language && (
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-white border border-[color:var(--line-strong)] text-[color:var(--ink-2)]">
                  {LANG_LABEL[session.native_language] || session.native_language}
                </span>
              )}
              {isClosed && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                  <Lock className="w-3 h-3" /> Closed · invitation only
                </span>
              )}
              {isTbc && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                  <Hourglass className="w-3 h-3" /> Time TBC
                </span>
              )}
            </div>

            <div className="flex items-start gap-2">
              <h1 data-testid="session-detail-title" className="font-serif-diplo text-2xl sm:text-3xl leading-tight text-[color:var(--ink)] flex-1">
                {title}
              </h1>
              <SpeakButton
                testId={`detail-speak-${session.id}`}
                text={`${title}. ${session.organization ? `Organized by ${session.organization}. ` : ""}${isTbc ? "Time to be confirmed" : `Starting at ${fmt(startTime)}`} at ${session.room}.`}
                label=""
                className="!p-2 flex-shrink-0"
              />
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-[color:var(--forest)]" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Date &amp; Time</div>
                  <div className="font-semibold">{fmtDate(startTime)}</div>
                  <div>{isTbc ? <span className="italic text-[color:var(--ink-2)]">Time to be confirmed · ~2h</span> : `${fmt(startTime)} – ${fmt(endTime)}`}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[color:var(--forest)]" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Venue</div>
                  <a href={mapLink} target="_blank" rel="noreferrer" className="font-semibold underline decoration-dotted underline-offset-2 hover:text-[color:var(--forest)]">
                    {session.room}
                  </a>
                  <div className="text-[color:var(--ink-2)]">Dakar, Senegal</div>
                </div>
              </div>
              {session.organization && (
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 mt-0.5 text-[color:var(--forest)]" />
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Organizer</div>
                    <div className="font-semibold text-[color:var(--forest-dark)]">{session.organization}</div>
                  </div>
                </div>
              )}
              {session.topic && (
                <div className="flex items-start gap-2">
                  <Tag className="w-4 h-4 mt-0.5 text-[color:var(--forest)]" />
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Topic</div>
                    <div className="italic text-[color:var(--ink-2)]">{session.topic}</div>
                  </div>
                </div>
              )}
            </div>

            {(session.focal_person || session.focal_person_2) && (
              <div className="mt-4 pt-4 border-t border-[color:var(--line)]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mb-2">Focal Contacts</div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  {session.focal_person && (
                    <div className="flex items-center gap-2" data-testid="detail-focal-1">
                      <Phone className="w-4 h-4 text-[color:var(--forest)]" />
                      <span className="font-semibold">{session.focal_person}</span>
                      {session.focal_contact && <span className="font-mono text-xs text-[color:var(--ink-2)]">· {session.focal_contact}</span>}
                    </div>
                  )}
                  {session.focal_person_2 && (
                    <div className="flex items-center gap-2" data-testid="detail-focal-2">
                      <Phone className="w-4 h-4 text-[color:var(--amber)]" />
                      <span className="font-semibold">{session.focal_person_2}</span>
                      {session.focal_contact_2 && <span className="font-mono text-xs text-[color:var(--ink-2)]">· {session.focal_contact_2}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isClosed && !isTbc && (
              <div className="mt-4 pt-4 border-t border-[color:var(--line)]">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">Seats</div>
                  <div className="text-xs font-mono">
                    <span className="font-bold">{session.reserved_count} / {session.capacity}</span>
                    {status === "limited" && <span className="ms-2 text-[color:var(--amber-dark)] font-bold">Only {remaining} left</span>}
                    {status === "full" && <span className="ms-2 text-rose-700 font-bold">Full · waitlist</span>}
                  </div>
                </div>
                <div className="capacity-bar">
                  <div className={`capacity-bar-fill ${status === "full" ? "is-full" : (remaining <= 5 ? "is-warn" : "")}`} style={{ width: `${Math.min(100, Math.round((session.reserved_count / Math.max(1, session.capacity)) * 100))}%` }} />
                </div>
              </div>
            )}

            <button
              data-testid="detail-reserve-btn"
              disabled={btnDisabled}
              onClick={() => setModal({ mode: "single", session })}
              className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-base font-bold tracking-wide transition-all
                ${btnDisabled
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : isAmber
                    ? "bg-[color:var(--amber)] text-white hover:bg-[color:var(--amber-dark)] shadow-lg shadow-amber-500/20"
                    : "bg-[color:var(--forest)] text-white hover:bg-[color:var(--forest-dark)] shadow-lg shadow-emerald-800/20"}`}
            >
              <Ticket className="w-5 h-5" /> {btnLabel}
            </button>
          </div>

          {description && description.trim().length > 0 && (
            <div className="diplomatic-card p-5 sm:p-6" data-testid="detail-description">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mb-2">About this session</div>
              <p className="text-sm leading-relaxed text-[color:var(--ink)] whitespace-pre-line">{description}</p>
            </div>
          )}
        </div>

        {/* Side column: map + related */}
        <div className="space-y-5">
          <div className="diplomatic-card overflow-hidden" data-testid="detail-map">
            <iframe
              title="Venue map"
              src={mapSrc}
              className="w-full h-56 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a href={mapLink} target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-[color:var(--forest)] hover:text-[color:var(--amber-dark)] transition-colors">
              <span>Open in Google Maps</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {related.length > 0 && (
            <div data-testid="detail-related">
              <div className="section-eyebrow mb-2">More from this organizer</div>
              <h2 className="font-serif-diplo text-base mb-3 text-[color:var(--ink)]">{session.organization}</h2>
              <div className="space-y-3">
                {related.map((s) => (
                  <SessionCard key={s.id} session={s} onReserve={(sess) => setModal({ mode: "single", session: sess })} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && <BookingModal modal={modal} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
    </div>
  );
}
